import React, { Fragment, PureComponent } from "react";
import { Box, Button, Divider, Grid, FormControl, FormControlLabel, Checkbox, Typography, Radio, RadioGroup, Tooltip } from "@material-ui/core";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";

import CustomAcordion from "../../../../layout/CustomAcordion";
import LabeledTextField from "../../shared/ui/HaklaiLabeledTextField";
import HelpDialog from "../../../../layout/HelpDialog";
import helpTexts from "../../../../../assets/texts/helpTexts";
import {
  getHaklaiPrisaPersonTitle,
  getHaklaiPrisaYearsBackLabel,
  HAKLAI_PRISA_ERRORS,
  HAKLAI_PRISA_LABELS,
  HAKLAI_PRISA_MESSAGES
} from "../haklaiPrisaTexts";
import {
  calculateHaklaiPrisaCreditPointsForPerson,
  HAKLAI_PRISA_CREDIT_POINT_GENDER,
  HAKLAI_PRISA_CREDIT_POINT_RESIDENCY
} from "../haklaiPrisaCreditPoints";

const CREDIT_POINT_HELPER_OFFSET = 20;
const PRISA_INCOME_FIELD_WIDTH = 96;
const PRISA_ZIKUI_FIELD_WIDTH = 64;
const PRISA_INCOME_MAX_VALUE = 9999999;
const PRISA_ZIKUI_MAX_VALUE = 100;
const CLIENTS_COUNT_FIELD_WIDTH = 170;
const CLIENTS_COUNT_VALIDATION_OFFSET = 20;
const CREDIT_POINT_YEAR_VALIDATION_OFFSET = 10;
const CREDIT_POINT_HELPER_ROW_STYLE = { marginBottom: 2 };
const CREDIT_POINT_BUTTON_STYLE = {
  height: 40,
  minWidth: 130
};
const CREDIT_POINTS_COMPACT_ERRORS = Object.freeze({
  [HAKLAI_PRISA_ERRORS.creditPointsRequired]: "חובה",
  [HAKLAI_PRISA_ERRORS.creditPointsInvalid]: "לא תקין"
});
const PRISA_YEAR_ROW_STYLE = { marginTop: 2 };
const CREDIT_POINT_GENDER_FIELD_WIDTH = 120;
const CREDIT_POINT_RESIDENCY_FIELD_WIDTH = 150;
const CREDIT_POINT_GENDER_OPTIONS = [
  { label: HAKLAI_PRISA_LABELS.selectPlaceholder, value: "" },
  { label: HAKLAI_PRISA_LABELS.male, value: HAKLAI_PRISA_CREDIT_POINT_GENDER.MALE },
  { label: HAKLAI_PRISA_LABELS.female, value: HAKLAI_PRISA_CREDIT_POINT_GENDER.FEMALE }
];
const CREDIT_POINT_RESIDENCY_OPTIONS = [
  { label: HAKLAI_PRISA_LABELS.selectPlaceholder, value: "" },
  { label: HAKLAI_PRISA_LABELS.israeliResident, value: HAKLAI_PRISA_CREDIT_POINT_RESIDENCY.ISRAELI },
  { label: HAKLAI_PRISA_LABELS.foreignResident, value: HAKLAI_PRISA_CREDIT_POINT_RESIDENCY.FOREIGN }
];

const getPrisaIncomeDisplayValue = (value) => (
  value === null || value === undefined || value === "" || Number.isNaN(value) ? 0 : value
);

class HaklaiPrisaComponent extends PureComponent {
  render() {
    const fieldErrors = this.props.fieldErrors || {};
    const useHaklaiValidationLayout = this.props.useHaklaiValidation === true;
    const clientsCountControlStyle = useHaklaiValidationLayout
      ? { transform: `translateY(${CLIENTS_COUNT_VALIDATION_OFFSET}px)` }
      : undefined;
    const creditPointYearInputStyle = useHaklaiValidationLayout
      ? { transform: `translateY(${CREDIT_POINT_YEAR_VALIDATION_OFFSET}px)` }
      : undefined;
    // Maximal Prisa displays the sale year as zero but keeps its draft values in state,
    // allowing the user's inputs to return unchanged when maximal mode is turned off.
    const isDisabledMaximalYear = (person, yearIndex) => yearIndex === 0 && person.isMaximal;
    const getFieldValidationProps = (fieldName) => (
      useHaklaiValidationLayout
        ? {
            helperTextMinHeight: 40,
            error: Boolean(fieldErrors[fieldName]),
            helperText: fieldErrors[fieldName] || " ",
            onBlur: () => this.handleFieldBlur(fieldName)
          }
        : {}
    );
    const getCreditPointsValidationProps = (fieldName) => {
      if (!useHaklaiValidationLayout) return {};
      const error = fieldErrors[fieldName];
      return {
        helperTextMinHeight: CREDIT_POINT_HELPER_OFFSET,
        helperTextSingleLine: true,
        centerHelperText: true,
        error: Boolean(error),
        helperText: error ? (CREDIT_POINTS_COMPACT_ERRORS[error] || "לא תקין") : " ",
        onBlur: () => this.handleFieldBlur(fieldName)
      };
    };
	    const creditPointHelperValidationProps = useHaklaiValidationLayout
	      ? { helperTextMinHeight: CREDIT_POINT_HELPER_OFFSET, helperText: " " }
	      : {};
	    const prisaYearsOptions = this.getPrisaYearsOptions(this.props.maxYears);
    const shouldShowClientsCount = (
      this.props.shouldTryPrisa
      && this.props.soldPropertyDate
      && this.props.maxYears >= 1
      && this.props.minYears !== 0
      && !this.props.isClientsNumDisabled
    );
    const accordionProps = {};
    if (typeof this.props.expanded === "boolean") {
      accordionProps.expanded = this.props.expanded;
      accordionProps.onToggleExpanded = this.props.onToggleExpanded;
      accordionProps.defaultExpanded = false;
    } else {
      accordionProps.defaultExpanded = true;
    }

    const mainGrid = (
      <Grid container direction="column" spacing={2}>
          <Grid container item direction="row" spacing={5} alignItems="center">
            {this.props.shouldTryPrisaShown ? (
              <Fragment>
                <Grid item data-testid="haklai-prisa-choice">
                  <FormControl component="fieldset" disabled={this.props.maxYears < 1 || this.props.minYears === 0}>
                    <Grid container direction="row" spacing={1} alignItems="center">
                      <Grid item>
                        <Typography variant="subtitle1" align="right">
                          {HAKLAI_PRISA_LABELS.shouldTryPrisa}
                        </Typography>
                      </Grid>
                      <Grid item>
                        <HelpDialog
                          helpDialog={helpTexts.shouldTryPrisa}
                          label={HAKLAI_PRISA_LABELS.shouldTryPrisa}
                        />
                      </Grid>
                      <Grid item>
                        <RadioGroup
                          row
                          name="shouldTryPrisa"
                          value={Boolean(this.props.shouldTryPrisa).toString()}
                          onChange={(e) => this.props.handlePrisaOnChange(e)}
                        >
                          <FormControlLabel
                            value={"true"}
                            control={<Radio color="primary" />}
                            label={HAKLAI_PRISA_LABELS.yes}
                            labelPlacement="end"
                          />
                          <FormControlLabel
                            value={"false"}
                            control={<Radio color="primary" />}
                            label={HAKLAI_PRISA_LABELS.no}
                            labelPlacement="end"
                          />
                        </RadioGroup>
                      </Grid>
                    </Grid>
                  </FormControl>
                </Grid>
              </Fragment>
            ) : null}
            {shouldShowClientsCount ? (
              <Grid item data-testid="haklai-prisa-clients-count">
                <LabeledTextField
                  type="select"
                  value={this.props.personsData.length}
                  textFieldWidth={CLIENTS_COUNT_FIELD_WIDTH}
                  name="personsData"
                  label={HAKLAI_PRISA_LABELS.clientsCount}
                  options={this.props.options}
                  stackLabelOnXs
                  labelBoxStyle={clientsCountControlStyle}
                  textFieldStyle={clientsCountControlStyle}
                  {...getFieldValidationProps("personsData")}
                  onChange={(e) => {
                    this.props.handlePrisaOnChange(e);
                  }}
                />
              </Grid>
            ) : null}
            {this.props.soldPropertyDateShown ? (
              <Grid item>
                <LabeledTextField
                  type="date"
                  value={this.props.soldPropertyDate}
                  textFieldWidth={170}
                  name="soldPropertyDate"
                  label={HAKLAI_PRISA_LABELS.soldPropertyDate}
                  stackLabelOnXs
                  validateInvalidInputOnBlur
                  onChange={(e) => {
                    this.props.handleOnChange(e);
                  }}
                />
              </Grid>
            ) : null}
            {this.props.datePropertyBoughtShown ? (
              <Grid item>
                <LabeledTextField
                  type="date"
                  value={this.props.datePropertyBought}
                  textFieldWidth={170}
                  name="datePropertyBought"
                  label={HAKLAI_PRISA_LABELS.datePropertyBought}
                  stackLabelOnXs
                  validateInvalidInputOnBlur
                  onChange={(e) => {
                    this.props.handleOnChange(e);
                  }}
                />
              </Grid>
            ) : null}
            {this.props.maxYears < 1 ? (
              <Grid item>
                <Grid container direction="row" alignItems="center" spacing={1}>
                  <Grid item>
                    <InfoOutlinedIcon color="secondary" style={{ verticalAlign: "middle" }} />
                  </Grid>
                  <Grid item>
                    <Typography variant="subtitle1" align="right">
                      {`${HAKLAI_PRISA_MESSAGES.sameSalePurchaseYearUnavailable}.`}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
	            ) : this.props.minYears === 0 ? (
	              <Grid item>
	                <Grid container direction="row" alignItems="center" spacing={1}>
                  <Grid item>
                    <InfoOutlinedIcon color="secondary" style={{ verticalAlign: "middle" }} />
                  </Grid>
                  <Grid item>
                    <Typography variant="subtitle1" align="right">
                      {`${HAKLAI_PRISA_MESSAGES.partialPurchaseInSaleYearUnavailable}.`}
                    </Typography>
                  </Grid>
                </Grid>
	              </Grid>
		            ) : null}
		          </Grid>
		          {this.props.shouldTryPrisa && this.props.soldPropertyDate
		            ? this.props.personsData.map((person, index) => {
                const calculatedCreditPoints = calculateHaklaiPrisaCreditPointsForPerson(person);
                const isCreditPointHelperDisabled = calculatedCreditPoints === null || !person.dataPerYear?.length;
                const creditPointTooltip = calculatedCreditPoints === null
                  ? HAKLAI_PRISA_LABELS.calculatedCreditPointsTooltip
                  : `${HAKLAI_PRISA_LABELS.calculatedCreditPointsTooltip}: ${calculatedCreditPoints}`;

                return (
                  <Fragment key={`prisa-person-data-${index}`}>
                    <Grid item>
                      <Divider className="haklai-prisa-divider" light />
                    </Grid>
                    {this.props.personsData.length > 1 ? (
                      <Grid item>
                        <Typography align="right" variant="h5" className="haklai-prisa-person-title">
                          {getHaklaiPrisaPersonTitle(index)}
                        </Typography>
                      </Grid>
                    ) : null}
                    <Grid container item direction="column" spacing={1} alignItems="flex-start">
	                      <Grid
	                        container
	                        item
		                        direction="row"
		                        spacing={2}
		                        alignItems={useHaklaiValidationLayout ? "center" : undefined}
		                      >
                        <Grid item>
                          <LabeledTextField
                            type="text"
                            value={person.personName}
                            textFieldWidth={170}
                            name="personName"
                            label={HAKLAI_PRISA_LABELS.clientName}
                            stackLabelOnXs
                            {...getFieldValidationProps(this.getPersonFieldKey("personName", index))}
                            onChange={(e) => this.props.handlePrisaOnChange(e, index)}
                          />
                        </Grid>
                        <Grid item>
                          <LabeledTextField
                            type="date"
                            value={person.birthOfClient}
                            textFieldWidth={170}
                            name="birthOfClient"
                            label={HAKLAI_PRISA_LABELS.birthOfClient}
                            stackLabelOnXs
                            validateInvalidInputOnBlur
                            {...getFieldValidationProps(this.getPersonFieldKey("birthOfClient", index))}
                            onChange={(e) => this.props.handlePrisaOnChange(e, index)}
                          />
                        </Grid>
                        <Grid item>
                          <LabeledTextField
                            type="select"
                            value={person.dataPerYear.length}
                            textFieldWidth={170}
                            name="dataPerYear"
                            label={HAKLAI_PRISA_LABELS.yearsCount}
                            options={prisaYearsOptions}
                            stackLabelOnXs
                            {...getFieldValidationProps(this.getPersonFieldKey("dataPerYear", index))}
                            onChange={(e) => this.props.handlePrisaOnChange(e, index)}
                          />
                        </Grid>
                      </Grid>
	                      <Grid
	                        container
		                        item
		                        direction="row"
			                        spacing={2}
				                        alignItems="flex-start"
				                        style={CREDIT_POINT_HELPER_ROW_STYLE}
				                      >
		                        <Grid item>
		                          <LabeledTextField
                            type="select"
                            value={person.creditPointGender || ""}
                            textFieldWidth={CREDIT_POINT_GENDER_FIELD_WIDTH}
                            name="creditPointGender"
		                            label={HAKLAI_PRISA_LABELS.creditPointGender}
		                            options={CREDIT_POINT_GENDER_OPTIONS}
                            displayEmptySelectOption
		                            stackLabelOnXs
		                            {...creditPointHelperValidationProps}
		                            onChange={(e) => this.props.handlePrisaOnChange(e, index)}
		                          />
		                        </Grid>
                        <Grid item>
                          <LabeledTextField
                            type="select"
                            value={person.creditPointResidency || ""}
                            textFieldWidth={CREDIT_POINT_RESIDENCY_FIELD_WIDTH}
                            name="creditPointResidency"
	                            label={HAKLAI_PRISA_LABELS.creditPointResidency}
	                            options={CREDIT_POINT_RESIDENCY_OPTIONS}
                            displayEmptySelectOption
	                            stackLabelOnXs
	                            {...creditPointHelperValidationProps}
	                            onChange={(e) => this.props.handlePrisaOnChange(e, index)}
	                          />
		                        </Grid>
			                        <Grid item>
		                          <Tooltip title={creditPointTooltip}>
	                            <span>
	                              <Button
                                type="button"
                                variant="contained"
	                                className="haklai-prisa-calc-button"
	                                disabled={isCreditPointHelperDisabled}
	                                style={CREDIT_POINT_BUTTON_STYLE}
	                                onClick={() => this.handleCreditPointsApply(index)}
	                              >
                                {HAKLAI_PRISA_LABELS.calculateCreditPoints}
                              </Button>
                            </span>
                          </Tooltip>
                        </Grid>
                      </Grid>
	                      <Grid item>
	                        <FormControlLabel
	                          className="haklai-prisa-maximal"
	                          style={{ marginRight: "-11px" }}
	                          labelPlacement="end"
	                          control={
	                            <Checkbox
	                              checked={person.isMaximal}
	                              value={!person.isMaximal}
	                              onChange={(e) => this.props.handlePrisaOnChange(e, index)}
	                              name="isMaximal"
	                              color="primary"
	                            />
	                          }
	                          label={HAKLAI_PRISA_LABELS.maximalSaleYearCalculation}
	                        />
	                      </Grid>
	                    </Grid>
                    {person.dataPerYear.map((year, yearIndex) => {
                      return (
	                        <Grid
	                          item
	                          key={`prisa-person-${index}-data-per-year-${yearIndex}`}
	                          style={PRISA_YEAR_ROW_STYLE}
	                        >
                          <Grid
                            className="haklai-prisa-year-row"
                            container
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Grid item>
                              <Typography
                                variant="body1"
                                color={yearIndex === 0 && person.isMaximal ? "textSecondary" : "textPrimary"}
                              >
                                {HAKLAI_PRISA_LABELS.taxYear}
                              </Typography>
                            </Grid>
                            <Grid item>
                              <Typography
                                variant="body1"
                                color={yearIndex === 0 && person.isMaximal ? "textSecondary" : "textPrimary"}
                              >
                                {parseInt(this.props.soldPropertyDate.substring(0, 4)) - yearIndex}
                              </Typography>
                            </Grid>
                            <Grid item>
                              <Typography
                                variant="body1"
                                color={yearIndex === 0 && person.isMaximal ? "textSecondary" : "textPrimary"}
                              >
                                {HAKLAI_PRISA_LABELS.income}
                              </Typography>
                            </Grid>
                            <Grid item>
                              <LabeledTextField
                                type="number"
                                textFieldWidth={PRISA_INCOME_FIELD_WIDTH}
                                value={isDisabledMaximalYear(person, yearIndex)
                                  ? 0
                                  : getPrisaIncomeDisplayValue(year.income)}
                                name="income"
                                max={PRISA_INCOME_MAX_VALUE}
                                disabled={isDisabledMaximalYear(person, yearIndex)}
                                inputBackgroundOnly={isDisabledMaximalYear(person, yearIndex)}
                                onChange={(e) => this.props.handlePrisaOnChange(e, index, yearIndex)}
                              />
                            </Grid>
                            <Grid item>
                              <Typography
                                variant="body1"
                                color={yearIndex === 0 && person.isMaximal ? "textSecondary" : "textPrimary"}
                              >
                                {HAKLAI_PRISA_LABELS.creditPoints}
                              </Typography>
                            </Grid>
                            <Grid item>
                              <LabeledTextField
                                type="number"
                                textFieldWidth={PRISA_ZIKUI_FIELD_WIDTH}
                                value={isDisabledMaximalYear(person, yearIndex) ? 0 : year.zikui}
                                name="zikui"
                                max={PRISA_ZIKUI_MAX_VALUE}
                                disabled={isDisabledMaximalYear(person, yearIndex)}
                                inputBackgroundOnly={isDisabledMaximalYear(person, yearIndex)}
                                textFieldStyle={creditPointYearInputStyle}
                                {...getCreditPointsValidationProps(this.getYearFieldKey("zikui", index, yearIndex))}
                                onChange={(e) => this.props.handlePrisaOnChange(e, index, yearIndex)}
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                      );
                    })}
                  </Fragment>
                );
              })
            : null}
      </Grid>
    );

    const wrappedMainContent = (
      <Box
        className={["haklai-prisa", this.props.contentWrapperClassName].filter(Boolean).join(" ")}
        ref={this.props.contentWrapperRef}
        inert={this.props.contentInert ? "" : undefined}
      >
        {mainGrid}
      </Box>
    );

    const AccordionShell = this.props.AccordionComponent || CustomAcordion;
    return (
      <AccordionShell summaryText={HAKLAI_PRISA_LABELS.sectionTitle} {...accordionProps}>
        {this.props.beforeContent || null}
        {wrappedMainContent}
      </AccordionShell>
    );
  }

  getPrisaYearsOptions(maxYears) {
    const options = [];
    for (let i = 1; i <= maxYears; i++) {
      options.push({ label: getHaklaiPrisaYearsBackLabel(i), value: i });
    }
    return options;
  }

  getPersonFieldKey(fieldName, personIndex) {
    return `${fieldName}_${personIndex}`;
  }

  getYearFieldKey(fieldName, personIndex, yearIndex) {
    return `${fieldName}_${personIndex}_${yearIndex}`;
  }

  handleFieldBlur(fieldName) {
    if (!fieldName || typeof this.props.onFieldBlur !== "function") return;
    this.props.onFieldBlur(fieldName);
  }

  handleCreditPointsApply(personIndex) {
    if (typeof this.props.handlePrisaCreditPointsApply !== "function") return;
    this.props.handlePrisaCreditPointsApply(personIndex);
  }
}

export default HaklaiPrisaComponent;
